import React, { useState, useEffect, useRef } from 'react';
import { TextField, Switch, Typography, Button } from '@mui/material';
import { MoreHorizOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WorkflowSessionModal from '../../../../ItemProperties/WorkflowSessionModal';

/**
 * Serialises a rows array to a plain object preserving order.
 * Exported so callers can use it for their own serialisation needs.
 */
export const rowsToObject = (rows) =>
    Object.fromEntries(rows.filter((r) => r.key).map((r) => [r.key, r.value ?? '']));

const parseToRows = (value, fallback) => {
    if (value) {
        try {
            const rows = Object.entries(JSON.parse(value)).map(([k, v]) => ({ key: k, value: v ?? '' }));
            if (rows.length) return rows;
        } catch { /* fall through */ }
    }
    return fallback;
};

/**
 * Self-contained key-value row editor with a Manual Input (raw text) toggle.
 *
 * All state, mode switching, and the WorkflowSessionModal are managed here.
 *
 * @param {string}   label           – section header (e.g. "Payload")
 * @param {Array}    defaultRows     – [{key, value}] used when defaultValue is absent
 * @param {string}   defaultValue    – initial serialised JSON (from connectionDetails)
 * @param {Object}   syncRowValues   – { rowKey: value } – overrides matching row values
 *                                     when the object reference changes (e.g. alg → algorithm)
 * @param {string}   addRowLabel     – placeholder text for the new-row key input
 * @param {Function} onChange        – (serialisedValue: string) => void
 * @param {boolean}  disableControl
 * @param {string}   screen          – shows workflow '...' buttons for GET_DATA / SEND_DATA
 */
const ManualOrRowInput = ({
    label,
    defaultRows = [],
    defaultValue,
    syncRowValues,
    addRowLabel,
    onChange,
    disableControl = false,
    screen,
}) => {
    const { t } = useTranslation();

    const [isManual, setIsManual] = useState(false);
    const [rows, setRows] = useState(() => parseToRows(defaultValue, defaultRows));
    const [textValue, setTextValue] = useState(() => {
        if (defaultValue) {
            try { return JSON.stringify(JSON.parse(defaultValue), null, 2); } catch { /* fall through */ }
        }
        return JSON.stringify(rowsToObject(defaultRows), null, 2);
    });

    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');

    // editMode tracks which field the workflow modal is targeting
    const [editMode, setEditMode] = useState(null); // { type: 'row', idx } | { type: 'text' }
    const [workflowModal, setWorkflowModal] = useState({ open: false, isFirstTime: false });

    // rowsRef keeps a non-stale reference for use inside effects
    const rowsRef = useRef(rows);
    useEffect(() => { rowsRef.current = rows; }, [rows]);

    // Re-initialize from parent when saved connection details reload (defaultValue only seeds useState)
    useEffect(() => {
        if (defaultValue === undefined) return;
        const newRows = parseToRows(defaultValue, defaultRows);
        setRows(newRows);
        try {
            setTextValue(JSON.stringify(JSON.parse(defaultValue || '{}'), null, 2));
        } catch {
            setTextValue(JSON.stringify(rowsToObject(newRows), null, 2));
        }
    // defaultRows intentionally omitted — only re-parse when saved payload reloads, not when template rows change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    // Sync specific row values from outside (e.g. alg row ↔ algorithm dropdown).
    // UI only — parent already updates JWT_PAYLOAD when the algorithm changes.
    useEffect(() => {
        if (!syncRowValues) return;
        const current = rowsRef.current;
        const updated = current.map((r) =>
            Object.prototype.hasOwnProperty.call(syncRowValues, r.key)
                ? { ...r, value: syncRowValues[r.key] }
                : r
        );
        const changed = updated.some((r, i) => r.value !== current[i]?.value);
        if (changed) {
            setRows(updated);
        }
    }, [syncRowValues]);

    const showModalBtns = ['GET_DATA', 'SEND_DATA'].includes(screen);

    const handleToggle = (manual) => {
        if (manual) {
            setTextValue(JSON.stringify(rowsToObject(rows), null, 2));
        } else {
            try {
                const parsed = JSON.parse(textValue);
                const newRows = Object.entries(parsed).map(([k, v]) => ({ key: k, value: v ?? '' }));
                if (newRows.length) {
                    setRows(newRows);
                    onChange(JSON.stringify(rowsToObject(newRows)));
                }
            } catch { /* keep current rows on parse failure */ }
        }
        setIsManual(manual);
    };

    const handleRowChange = (idx, field, value) => {
        const updated = rows.map((r, i) => i === idx ? { ...r, [field]: value } : r);
        setRows(updated);
        onChange(JSON.stringify(rowsToObject(updated)));
    };

    const handleCommitNewRow = () => {
        if (!newKey.trim()) return;
        const updated = [...rows, { key: newKey.trim(), value: newValue }];
        setRows(updated);
        onChange(JSON.stringify(rowsToObject(updated)));
        setNewKey('');
        setNewValue('');
    };

    const valuesHandler = (value) => {
        if (editMode?.type === 'row') {
            const updated = rows.map((r, i) => i === editMode.idx ? { ...r, value } : r);
            setRows(updated);
            onChange(JSON.stringify(rowsToObject(updated)));
        } else if (editMode?.type === 'newRow') {
            setNewValue(value);
            if (newKey.trim()) {
                const updated = [...rows, { key: newKey.trim(), value }];
                setRows(updated);
                onChange(JSON.stringify(rowsToObject(updated)));
                setNewKey('');
                setNewValue('');
            }
        } else if (editMode?.type === 'text') {
            setTextValue(value);
            onChange(value);
        }
    };

    const modalInitialValue =
        editMode?.type === 'row' ? rows[editMode.idx]?.value ?? ''
        : editMode?.type === 'newRow' ? newValue
        : editMode?.type === 'text' ? textValue
        : '';

    const modalLabel =
        editMode?.type === 'row' ? rows[editMode.idx]?.key ?? ''
        : label ?? '';

    return (
        <>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    {label && <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography variant="caption" color="text.secondary">{t('JWT_MANUAL_INPUT')}</Typography>
                        <Switch
                            size="small"
                            checked={isManual}
                            disabled={disableControl}
                            onChange={(e) => handleToggle(e.target.checked)}
                        />
                    </div>
                </div>

                {isManual ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                        <TextField
                            variant="outlined" size="small" multiline minRows={4}
                            value={textValue} disabled={disableControl}
                            onChange={(e) => { setTextValue(e.target.value); onChange(e.target.value); }}
                            className="width-100"
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                        />
                        {showModalBtns && (
                            <Button size="small" variant="outlined" disabled={disableControl}
                                onClick={() => { setEditMode({ type: 'text' }); setWorkflowModal({ open: true, isFirstTime: true }); }}
                                sx={{ width: '40px', minWidth: '40px' }}>
                                <MoreHorizOutlined fontSize="small" />
                            </Button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {rows.map((row, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '4px' }}>
                                <TextField variant="outlined" size="small" value={row.key}
                                    disabled={disableControl}
                                    onChange={(e) => handleRowChange(idx, 'key', e.target.value)}
                                    sx={{ flex: 1 }} />
                                <TextField variant="outlined" size="small" value={row.value}
                                    disabled={disableControl}
                                    onChange={(e) => handleRowChange(idx, 'value', e.target.value)}
                                    sx={{ flex: 1 }} />
                                {showModalBtns && (
                                    <Button size="small" variant="outlined" disabled={disableControl}
                                        onClick={() => { setEditMode({ type: 'row', idx }); setWorkflowModal({ open: true, isFirstTime: true }); }}
                                        sx={{ width: '40px', minWidth: '40px' }}>
                                        <MoreHorizOutlined fontSize="small" />
                                    </Button>
                                )}
                            </div>
                        ))}

                        {/* Add row */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <TextField variant="outlined" size="small" value={newKey}
                                placeholder={addRowLabel} disabled={disableControl}
                                onChange={(e) => setNewKey(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCommitNewRow(); }}
                                sx={{ flex: 1 }} />
                            <TextField variant="outlined" size="small" value={newValue}
                                disabled={disableControl}
                                onChange={(e) => setNewValue(e.target.value)}
                                onBlur={() => { if (newKey.trim()) handleCommitNewRow(); }}
                                sx={{ flex: 1 }} />
                            {showModalBtns && (
                                <Button size="small" variant="outlined" disabled={disableControl}
                                    onClick={() => { setEditMode({ type: 'newRow' }); setWorkflowModal({ open: true, isFirstTime: true }); }}
                                    sx={{ width: '40px', minWidth: '40px' }}>
                                    <MoreHorizOutlined fontSize="small" />
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <WorkflowSessionModal
                valueLabel={modalLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowModal}
                setModalOpen={setWorkflowModal}
                handleClose={() => setWorkflowModal({ open: false, isFirstTime: false })}
                initialValue={modalInitialValue}
                inputType="text"
                valuesHandler={valuesHandler}
            />
        </>
    );
};

export default ManualOrRowInput;
